import React, { useEffect, useRef, memo } from 'react';
import { useIsDark } from '../../ui/ThemeToggle';

// Map our coin symbols to TradingView symbols
const TV_SYMBOL: Record<string, string> = {
  BTC:  'BINANCE:BTCUSDT',
  ETH:  'BINANCE:ETHUSDT',
  XRP:  'BINANCE:XRPUSDT',
  SOL:  'BINANCE:SOLUSDT',
  BNB:  'BINANCE:BNBUSDT',
  ADA:  'BINANCE:ADAUSDT',
  DOGE: 'BINANCE:DOGEUSDT',
  DOT:  'BINANCE:DOTUSDT',
  USDT: 'BINANCE:USDTUSD',
};

interface TradingViewChartProps {
  symbol: string;   // e.g. 'BTC'
  height?: number;
}

declare global {
  interface Window {
    TradingView?: any;
  }
}

const TradingViewChart: React.FC<TradingViewChartProps> = ({ symbol, height = 400 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = useIsDark();
  const widgetRef   = useRef<any>(null);
  const scriptId    = 'tradingview-widget-script';

  const tvSymbol = TV_SYMBOL[symbol] || `BINANCE:${symbol}USDT`;

  const buildWidget = () => {
    if (!containerRef.current || !window.TradingView) return;

    // Clear old widget
    containerRef.current.innerHTML = '';

    widgetRef.current = new window.TradingView.widget({
      autosize: true,
      symbol: tvSymbol,
      interval: '60',          // 1H default
      timezone: 'Etc/UTC',
      theme: isDark ? 'dark' : 'light',
      style: '1',               // Candles
      locale: 'en',
      toolbar_bg: isDark ? '#0A0A0A' : '#FFFFFF',
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      container_id: containerRef.current.id,
      backgroundColor: isDark ? 'rgba(10, 10, 10, 0)' : 'rgba(255, 255, 255, 0)',
      gridColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(24, 26, 32, 0.06)',
      overrides: {
        'paneProperties.background': isDark ? '#0A0A0A' : '#FFFFFF',
        'paneProperties.backgroundType': 'solid',
        'scalesProperties.textColor': '#848E9C',
        'mainSeriesProperties.candleStyle.upColor':   isDark ? '#B6E050' : '#86C442',
        'mainSeriesProperties.candleStyle.downColor': '#E75560',
        'mainSeriesProperties.candleStyle.borderUpColor':   isDark ? '#B6E050' : '#86C442',
        'mainSeriesProperties.candleStyle.borderDownColor': '#E75560',
        'mainSeriesProperties.candleStyle.wickUpColor':   isDark ? '#B6E05080' : '#86C44280',
        'mainSeriesProperties.candleStyle.wickDownColor': '#E7556080',
      },
      studies: ['Volume@tv-basicstudies'],
      withdateranges: true,
      allow_symbol_change: false,
      details: true,
      hotlist: false,
      calendar: false,
      news: [],
    });
  };

  useEffect(() => {
    // Load TradingView script once
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id   = scriptId;
      script.src  = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = buildWidget;
      document.head.appendChild(script);
    } else if (window.TradingView) {
      buildWidget();
    } else {
      // Script already loading, poll
      const interval = setInterval(() => {
        if (window.TradingView) {
          clearInterval(interval);
          buildWidget();
        }
      }, 200);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, isDark]);

  const containerId = `tv_chart_${symbol}`;

  return (
    <div
      id={containerId}
      ref={containerRef}
      className="w-full rounded-xl overflow-hidden bg-card"
      style={{ height }}
    />
  );
};

export default memo(TradingViewChart);
